import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  ConfirmDialog,
  Input,
  Stat,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableTd,
  TableTh,
  Badge,
  useToast,
} from '../../components/ui';

const ADMIN_EMAIL = 'nikodeveloper23@gmail.com';

interface AdminUser {
  identityId: string;
  tUserId?: number;
  email: string;
  userName: string;
  profileName?: string;
  role: string;
  roleId: number;
  mobile?: string;
  country?: string;
  specialty?: string;
  company?: string;
  registeredAt: string;
  isGoogleAccount: boolean;
  hasTUser: boolean;
}

export function AdminPanel() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; email: string } | null>(null);

  useEffect(() => {
    void checkAdminAndLoadUsers();
  }, []);

  const checkAdminAndLoadUsers = async () => {
    try {
      const adminCheckResponse = await axios.get('/api/admin/check-admin');
      const { isAdmin: userIsAdmin } = adminCheckResponse.data;

      if (!userIsAdmin) {
        setError('Access Denied: You are not authorized to view this page.');
        setLoading(false);
        return;
      }

      setIsAdmin(true);

      const response = await axios.get('/api/admin/users');
      setUsers(response.data.users);
      setLoading(false);
    } catch (err: unknown) {
      console.error('Admin panel error:', err);
      const msg =
        axios.isAxiosError(err) && err.response?.data?.error
          ? String(err.response.data.error)
          : 'Failed to load admin panel';
      setError(msg);
      setLoading(false);
    }
  };

  const requestDelete = (identityId: string, email: string) => {
    setPendingDelete({ id: identityId, email });
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const { id, email } = pendingDelete;
    try {
      setDeletingUserId(id);
      await axios.delete(`/api/admin/users/${id}`);
      setUsers((prev) => prev.filter((u) => u.identityId !== id));
      showToast(`User ${email} deleted`, 'success');
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) && err.response?.data?.error
          ? String(err.response.data.error)
          : 'Failed to delete user';
      showToast(msg, 'error');
    } finally {
      setDeletingUserId(null);
      setConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '0001-01-01T00:00:00') return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.profileName && u.profileName.toLowerCase().includes(q)) ||
        (u.country && u.country.toLowerCase().includes(q)) ||
        (u.specialty && u.specialty.toLowerCase().includes(q)),
    );
  }, [users, search]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stitch-canvas">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-stitch-primary border-t-transparent" />
          <p className="mt-4 text-stitch-muted">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (error || !isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stitch-canvas p-4">
        <div className="w-full max-w-md rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 text-center shadow-stitch-card">
          <h3 className="text-xl font-bold text-stitch-fg">Access Denied</h3>
          <p className="mt-2 text-stitch-fg-soft">{error || 'You are not authorized.'}</p>
          <Button type="button" className="mt-6" onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stitch-canvas">
      <div className="border-b border-stitch-border bg-gradient-to-br from-stitch-accent-soft via-stitch-surface to-stitch-primary-soft">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-10 sm:flex-row sm:items-end sm:justify-between sm:px-6 lg:px-8">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-stitch-accent">
              <span className="material-symbols-outlined text-base">admin_panel_settings</span>
              Operations
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-stitch-fg md:text-4xl">Admin</h1>
            <p className="mt-2 text-stitch-muted">Manage InterviewR users</p>
          </div>
          <Input
            className="max-w-md sm:w-80"
            placeholder="Search email, name, country…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <Stat label="Total users" value={users.length} />
        <Stat
          label="Interviewers"
          value={users.filter((u) => u.role === 'Interviewer').length}
        />
        <Stat
          label="Interviewees"
          value={users.filter((u) => u.role === 'Interviewee').length}
        />
      </div>

      <Table>
        <TableHead>
          <TableRow>
            <TableTh>Email / Profile</TableTh>
            <TableTh>Role</TableTh>
            <TableTh>Location</TableTh>
            <TableTh>Specialty</TableTh>
            <TableTh>Registered</TableTh>
            <TableTh>Type</TableTh>
            <TableTh className="text-right">Actions</TableTh>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((user) => (
            <TableRow key={user.identityId}>
              <TableTd>
                <div className="font-semibold text-stitch-fg">{user.email}</div>
                {user.profileName && (
                  <div className="text-xs text-stitch-muted">{user.profileName}</div>
                )}
                {user.mobile && (
                  <div className="text-xs text-stitch-muted">{user.mobile}</div>
                )}
              </TableTd>
              <TableTd>
                <Badge variant={user.role === 'Interviewer' ? 'success' : 'primary'}>
                  {user.role}
                </Badge>
              </TableTd>
              <TableTd>{user.country ?? '—'}</TableTd>
              <TableTd>
                <div className="max-w-xs truncate">{user.specialty ?? '—'}</div>
                {user.company && (
                  <div className="text-xs text-stitch-muted">{user.company}</div>
                )}
              </TableTd>
              <TableTd className="text-stitch-fg-soft">
                {formatDate(user.registeredAt)}
              </TableTd>
              <TableTd>
                <Badge variant={user.isGoogleAccount ? 'warning' : 'default'}>
                  {user.isGoogleAccount ? 'Google' : 'Email'}
                </Badge>
              </TableTd>
              <TableTd className="text-right">
                {user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase() ? (
                  <span className="text-xs text-stitch-muted">Protected</span>
                ) : (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    disabled={deletingUserId === user.identityId}
                    onClick={() => requestDelete(user.identityId, user.email)}
                  >
                    {deletingUserId === user.identityId ? 'Deleting…' : 'Delete'}
                  </Button>
                )}
              </TableTd>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {filtered.length === 0 && (
        <p className="mt-6 text-center text-stitch-muted">No users match your search.</p>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete user?"
        description={
          pendingDelete
            ? `This will permanently remove ${pendingDelete.email} and related data. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        loading={!!deletingUserId}
      />
      </div>
    </div>
  );
}
